import type {CollectionOwnersViewADto} from '@cohort/admin-schemas/digitalAssetCollection';
import PolygonIcon from '@cohort/merchants/assets/images/polygon.png';
import Loader from '@cohort/merchants/components/Loader';
import type {TableEmptyState} from '@cohort/merchants/components/Table';
import Table from '@cohort/merchants/components/Table';
import OwnershipObtentionTypeBadge from '@cohort/merchants/pages/digital-asset-collections/digital-asset-collection/owners/OwnershipObtentionTypeBadge';
import type {Network} from '@cohort/shared/schema/common/blockchain';
import {blockchainExplorerUrl} from '@cohort/shared/utils/blockchain';
import {formatDate} from '@cohort/shared/utils/format';
import React from 'react';
import {useTranslation} from 'react-i18next';

type DigitalAssetCollectionOwnersTableProps = {
  owners: Array<CollectionOwnersViewADto>;
  isLoading: boolean;
  nftEnabled: boolean;
  emptyState?: TableEmptyState;
};

type TableCellProps = {
  owner: CollectionOwnersViewADto;
};

const AssetCell: React.FC<TableCellProps> = ({owner}) => (
  <td data-testid="ownership-id" className="py-4 pr-3 text-sm font-medium">
    #{owner.digitalAsset.tokenId}
  </td>
);

const OwnerCell: React.FC<TableCellProps & {nftEnabled: boolean}> = ({owner, nftEnabled}) => {
  function handleAddressClick(network: Network, address: string): void {
    const baseUrl = blockchainExplorerUrl(network);

    window.open(`${baseUrl}/address/${address}#tokentxnsErc1155`, '_blank');
  }

  return (
    <td
      data-testid="ownership-user"
      className="mx-auto flex items-center gap-2 whitespace-nowrap py-4 pr-3 text-sm text-slate-500"
    >
      {owner.digitalAsset.nft?.network && owner.wallet && (
        <button
          data-testid="order-trx-address"
          onClick={() =>
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            handleAddressClick(owner.digitalAsset.nft!.network, owner.wallet!.address)
          }
        >
          <img src={PolygonIcon} alt="Polygon" className="h-4 w-4" />
        </button>
      )}
      {nftEnabled && owner.digitalAsset.nft === null && <Loader size={16} color="gray" />}
      {owner.user?.email ?? <span className="font-semibold italic">External wallet</span>}
    </td>
  );
};

const OriginCell: React.FC<TableCellProps> = ({owner}) => {
  return (
    <td data-testid="ownership-origin">
      <OwnershipObtentionTypeBadge
        obtentionType={owner.obtentionType}
        campaignName={owner.campaign?.internalName}
        campaignType={owner.campaign?.type}
      />
    </td>
  );
};

const DateCell: React.FC<TableCellProps> = ({owner}) => (
  <td
    data-testid="ownership-date"
    className="mx-auto whitespace-nowrap py-4 pr-3 text-sm text-slate-500"
  >
    {owner.createdAt ? formatDate(owner.createdAt) : '-'}
  </td>
);

const DigitalAssetCollectionOwnersTable: React.FC<DigitalAssetCollectionOwnersTableProps> = ({
  owners,
  nftEnabled,
  isLoading,
  emptyState,
}) => {
  const {t} = useTranslation('pages', {
    keyPrefix:
      'digitalAssetCollections.digitalAssetCollection.owners.digitalAssetCollectionOwnersTable',
  });

  const headers = [
    {label: t('tableHeaderDigitalAsset'), width: '100px'},
    {label: t('tableHeaderOwner'), width: '300px'},
    {label: t('tableHeaderOrigin')},
    {label: t('tableHeaderDate'), width: '250px'},
  ];

  return (
    <Table headers={headers} isLoading={isLoading} emptyState={emptyState}>
      {owners.map(owner => (
        <tr key={owner.digitalAsset.id}>
          <AssetCell owner={owner} />
          <OwnerCell owner={owner} nftEnabled={nftEnabled} />
          <OriginCell owner={owner} />
          <DateCell owner={owner} />
        </tr>
      ))}
    </Table>
  );
};

export default DigitalAssetCollectionOwnersTable;
